import {
	CampaignTypeSchema,
	DynamicContentFilterSchema,
	MediaObjectSchema,
	CampaignSendWindowSchema,
	MessagingReportSchema,
	ChangesSchema,
	TriggersSchema,
	RecipeInfoSchema,
	EmailTemplateSchema,
	BrowserTemplateSchema,
	PushTemplateSchema,
	VoiceTemplateSchema,
	SnailMailTemplateSchema,
	SMSTemplateSchema,
	AdTemplateSchema,
	ChannelTemplateSchema,
	LiteTemplateSchema,
	XLiteTemplateSchema,
	DateRangeSchema,
	CampaignChangeSetSchema,
	CampaignSchema,
	CampaignSortFieldsSchema,
	CampaignWithChangeSetSchema,
	CampaignUIDSchema,
	SendingTypeSchema,
	ABVariantSchema,
	VoiceDropLanguageSchema,
	StaticProductSchema,
	ChannelConfigSchema,
	CampaignScheduleSchema,
	CampaignTargetingSchema,
	CampaignMessageCapsSchema,
	LegacyOptionTypeSchema,
	StandardOptionTypeSchema,
	Restricted10dlcOptionTypeSchema,
	OptionTypeSchema,
	FullMessagePreviewSchema,
	MessagePreviewSchema,
	ProductFeedTypeSchema,
	FullCampaignQuerySchema,
	PaginationSchema,
	CampaignFiltersSchema,
	CampaignsQueryResponseSchema,
	CampaignChannelTypeSchema,
} from '@/schemas';

import type { z } from 'zod';
import { InstalledCoMarketingTemplate, SponsoredCoMarketingTemplate } from './coMarketing';

export type CampaignType = z.infer<typeof CampaignTypeSchema>;
export type DynamicContentFilter = z.infer<typeof DynamicContentFilterSchema>;
export type MediaObject = z.infer<typeof MediaObjectSchema>;
export type CampaignSendWindow = z.infer<typeof CampaignSendWindowSchema>;
export type MessagingReport = z.infer<typeof MessagingReportSchema>;
export type Changes = z.infer<typeof ChangesSchema>;
export type Triggers = z.infer<typeof TriggersSchema>;
export type RecipeInfo = z.infer<typeof RecipeInfoSchema>;
export type EmailTemplate = z.infer<typeof EmailTemplateSchema>;
export type BrowserTemplate = z.infer<typeof BrowserTemplateSchema>;
export type PushTemplate = z.infer<typeof PushTemplateSchema>;
export type VoiceTemplate = z.infer<typeof VoiceTemplateSchema>;
export type SnailMailTemplate = z.infer<typeof SnailMailTemplateSchema>;
export type SMSTemplate = z.infer<typeof SMSTemplateSchema>;
export type AdTemplate = z.infer<typeof AdTemplateSchema>;
export type ChannelTemplate = z.infer<typeof ChannelTemplateSchema>;
export type LiteTemplate = z.infer<typeof LiteTemplateSchema>;
export type XLiteTemplate = z.infer<typeof XLiteTemplateSchema>;
export type DateRange = z.infer<typeof DateRangeSchema>;
export type CampaignChangeSet = z.infer<typeof CampaignChangeSetSchema>;
export type Campaign = z.infer<typeof CampaignSchema>;
export type CampaignSortFields = z.infer<typeof CampaignSortFieldsSchema>;
export type CampaignWithChangeSet = z.infer<typeof CampaignWithChangeSetSchema>;
export type CampaignUID = z.infer<typeof CampaignUIDSchema>;
export type CampaignSendingType = z.infer<typeof SendingTypeSchema>;
export type ABVariant = z.infer<typeof ABVariantSchema>;
export type CampaignsType = CampaignUID | Campaign;
export type VoiceDropLanguage = z.infer<typeof VoiceDropLanguageSchema>;
export type StaticProduct = z.infer<typeof StaticProductSchema>;
export type ChannelConfig = z.infer<typeof ChannelConfigSchema>;
export type CampaignSchedule = z.infer<typeof CampaignScheduleSchema>;
export type CampaignTargeting = z.infer<typeof CampaignTargetingSchema>;
export type CampaignMessageCaps = z.infer<typeof CampaignMessageCapsSchema>;
export type LegacyOptionType = z.infer<typeof LegacyOptionTypeSchema>;
export type StandardOptionType = z.infer<typeof StandardOptionTypeSchema>;
export type Restricted10dlcOptionType = z.infer<typeof Restricted10dlcOptionTypeSchema>;
export type TextingOptionType = z.infer<typeof OptionTypeSchema>;
export type FullMessagePreview = z.infer<typeof FullMessagePreviewSchema>;
export type MessagePreview = z.infer<typeof MessagePreviewSchema>;
export type ProductFeedType = z.infer<typeof ProductFeedTypeSchema>;
export type FullCampaignQuery = z.infer<typeof FullCampaignQuerySchema>;
export type Pagination = z.infer<typeof PaginationSchema>;
export type CampaignFilters = z.infer<typeof CampaignFiltersSchema>;
export type CampaignsQueryResponse = z.infer<typeof CampaignsQueryResponseSchema>;
export type CampaignChannelTypeEnum = z.infer<typeof CampaignChannelTypeSchema>;

export type CampaignsV2Response = {
	campaign: Partial<Campaign>;
	sponsoredTemplate?: Partial<SponsoredCoMarketingTemplate>;
	installedTemplate?: Partial<InstalledCoMarketingTemplate>;
	templateType?: 'sponsored' | 'installed' | undefined;
};

export enum CampaignChannelType {
	Email = 'emailTemplate',
	Text = 'templates',
	Voice = 'voiceTemplate',
	Push = 'pushTemplate',
	Browser = 'browserTemplate',
	SnailMail = 'snailMailTemplate',
	Ad = 'adTemplate',
}

export const CampaignChannelTypes = [
	CampaignChannelType.Email,
	CampaignChannelType.Text,
	CampaignChannelType.Voice,
	CampaignChannelType.Push,
	CampaignChannelType.Browser,
] as CampaignChannelType[];

export type ABChannelType = CampaignChannelType.Email | CampaignChannelType.Text;
export type ABChannel = EmailTemplate | SMSTemplate;

export type CampaignChannelUnion = EmailTemplate | VoiceTemplate | SMSTemplate | PushTemplate | BrowserTemplate | SnailMailTemplate | AdTemplate;

export enum ConversionActionType {
	text_received = 'text_received',
	text_click_to_open = 'text_click_to_open',
	text_sent = 'text_sent',
	text_click = 'text_click',
	email_received = 'email_received',
	email_open = 'email_open',
	push_received = 'push_received',
	push_open = 'push_open',
	// ad_received = '// ad_received',
	browser_received = 'browser_received',
	direct_mail_received = 'direct_mail_received',
	TerpliReview = 'TerpliReview',
	TerpliRecommendation = 'TerpliRecommendation',
	TerpliReviewDiscount = 'TerpliReviewDiscount',
	TerpliRecommendationDiscount = 'TerpliRecommendationDiscount',
	call_answer = 'call_answer',
	call_vm = 'call_vm',
	call_complete = 'call_complete',
}

export enum CampaignTriggerType {
	normal = 'normal',
	abandonedCart = 'abandonedCart',
	postPurchase = 'postPurchase',
	birthdayReward = 'birthdayReward',
	randomLottery = 'randomLottery',
}

export enum CampaignScheduleType {
	Blast = 'blast',
	Ongoing = 'ongoing',
	DateRange = 'dateRange',
	Queue = 'queue',
}

export type PutCampaignV2Args = {
	bypassValidation?: boolean;
	coMarketingBypass?: boolean;
	coMarketingEditBypass?: boolean;
	bypassLocked?: boolean;
	bypassArchived?: boolean;
};
